import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatSnackBar } from '@angular/material';
import { GatewayService } from 'src/app/gateway.service';

/**
 * Dialog de confirmacion para Aprobar/Cerrar una alarma. Cascada de 3 niveles
 * (tipologia -> subtipologia -> razon) restringida al arbol "Infructuosa"
 * (raiz.nivelcontacto = 2) filtrando por area_id de la cita.
 *
 * data:
 *   {
 *     accion: 1 | 2,
 *     id_cita: number,
 *     idtipologia_actual: number,
 *     idsubtipologia_actual: number,
 *     idrazon_actual?: number,   // razon ya tipificada en la alarma
 *   }
 *
 * Retorna al confirmar:
 *   {
 *     resp: true,
 *     idtipologia_agente: number,
 *     idsubtipologia_agente: number,
 *     idrazon_agente: number | null
 *   }
 */
@Component({
  selector: 'app-alarma-confirmacion-dialog',
  templateUrl: './alarma-confirmacion-dialog.component.html',
  styleUrls: ['./alarma-confirmacion-dialog.component.scss']
})
export class AlarmaConfirmacionDialogComponent implements OnInit {

  form: FormGroup;
  cargando = false;
  mensaje = '';

  // Catalogo plano desde backend (todos los niveles del arbol Infructuosa)
  todas: any[] = [];

  tipologias: any[] = [];     // nivel jerarquico 1 (raiz)
  subtipologias: any[] = [];  // nivel 2 hijas de la tipologia seleccionada
  razones: any[] = [];        // nivel 3 hijas de la subtipologia seleccionada

  constructor(
    private fb: FormBuilder,
    private gateway: GatewayService,
    private snackbar: MatSnackBar,
    public dialogRef: MatDialogRef<AlarmaConfirmacionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Titulo dinamico: depende del mode y del id_contrata (41 Orden, 44 Falla)
    const esOrden = Number(data.id_contrata) === 41;
    const entidad = esOrden ? 'orden' : 'falla';
    let accionTxt: string;
    switch (data.mode) {
      case 'cerrar_orden':
        accionTxt = `CERRAR la ${entidad}`;
        break;
      case 'aprobar':
        accionTxt = 'APROBAR la solicitud';
        break;
      case 'rechazar':
        accionTxt = 'RECHAZAR la solicitud';
        break;
      default:
        accionTxt = Number(data.accion) === 1 ? 'APROBAR la solicitud' : 'RECHAZAR la solicitud';
    }
    this.mensaje = `¿Seguro que desea ${accionTxt}?`;

    this.form = this.fb.group({
      idtipologia:    [null, Validators.required],
      idsubtipologia: [null, Validators.required],
      idrazon:        [null]
    });
  }

  ngOnInit() {
    this.cargarTipologias();
  }

  cargarTipologias() {
    this.cargando = true;
    this.gateway.get('getTipologiasAlarmaPorCita', {
      id_cita: this.data.id_cita,
      tipo_gestion: this.data.tipo_gestion || 1
    }).then((res: any) => {
      this.todas = Array.isArray(res.rows) ? res.rows : [];
      this.tipologias = this.todas.filter(t => t.nivel_tipologia === 1);
      this.preseleccionar();
      this.cargando = false;
    }).catch(() => {
      this.todas = [];
      this.tipologias = [];
      this.cargando = false;
    });
  }

  /**
   * Navega el arbol a partir de los ids actuales de la alarma para precargar
   * los 3 dropdowns. Prioridad para encontrar el candidato mas profundo:
   *   1. idrazon_actual (nivel 3, la razon de la alarma original/agente)
   *   2. idsubtipologia_actual (nivel 2)
   *   3. idtipologia_actual (nivel 1)
   */
  preseleccionar() {
    const idTipActual = Number(this.data.idtipologia_actual);
    const idSubActual = Number(this.data.idsubtipologia_actual);
    const idRazonActual = Number(this.data.idrazon_actual);

    const candidato =
      this.findById(idRazonActual) ||
      this.findById(idSubActual) ||
      this.findById(idTipActual);
    if (!candidato) { return; }

    let nivel1 = null, nivel2 = null, nivel3 = null;
    if (candidato.nivel_tipologia === 3) {
      nivel3 = candidato;
      nivel2 = this.findById(candidato.idtipologiapadre);
      if (nivel2) { nivel1 = this.findById(nivel2.idtipologiapadre); }
    } else if (candidato.nivel_tipologia === 2) {
      nivel2 = candidato;
      nivel1 = this.findById(candidato.idtipologiapadre);
    } else {
      nivel1 = candidato;
    }

    if (nivel1) {
      this.form.patchValue({ idtipologia: nivel1.idtipologia });
      this.onTipologiaChange(nivel1.idtipologia);
    }
    if (nivel2) {
      this.form.patchValue({ idsubtipologia: nivel2.idtipologia });
      this.onSubtipologiaChange(nivel2.idtipologia);
    }
    if (nivel3) {
      this.form.patchValue({ idrazon: nivel3.idtipologia });
    }
  }

  findById(id: number): any {
    if (!id) { return null; }
    return this.todas.find(t => Number(t.idtipologia) === Number(id)) || null;
  }

  onTipologiaChange(idTip: any) {
    this.subtipologias = this.todas.filter(t => Number(t.idtipologiapadre) === Number(idTip) && t.nivel_tipologia === 2);
    this.razones = [];
    this.form.patchValue({ idsubtipologia: null, idrazon: null });
  }

  onSubtipologiaChange(idSub: any) {
    this.razones = this.todas.filter(t => Number(t.idtipologiapadre) === Number(idSub) && t.nivel_tipologia === 3);
    this.form.patchValue({ idrazon: null });
  }

  puedeConfirmar(): boolean {
    const v = this.form.value;
    if (!v.idtipologia || !v.idsubtipologia) { return false; }
    // La razon solo es obligatoria si hay opciones (si la subtipologia elegida tiene hijos nivel 3)
    if (this.razones.length > 0 && !v.idrazon) { return false; }
    return true;
  }

  confirmar() {
    if (!this.puedeConfirmar()) {
      this.snackbar.open('Complete todos los campos', 'Cerrar', { duration: 2500 });
      return;
    }
    const v = this.form.value;
    this.dialogRef.close({
      resp: true,
      idtipologia_agente: Number(v.idtipologia) || null,
      idsubtipologia_agente: Number(v.idsubtipologia) || null,
      idrazon_agente: v.idrazon ? Number(v.idrazon) : null
    });
  }

  cancelar() {
    this.dialogRef.close({ resp: false });
  }
}
